export interface GVIntelligenceSource {
  index: string;
  source: string;
}

export interface GVIntelligenceResult {
  answer: string;
  sources: GVIntelligenceSource[];
  timestamp?: string;
  model?: string;
}

export const GV_INTELLIGENCE_API_URL = "https://carly-cloisterlike-unapologetically.ngrok-free.dev/webhook/gv-chat";

export function resolveGVIntelligenceMode(query: string): "global" | "hybrid" {
  const normalizedQuery = query.toLowerCase();
  return normalizedQuery.includes("overzicht") || normalizedQuery.includes("alle ") ? "global" : "hybrid";
}

function extractErrorMessage(payload: unknown): string | null {
  if (typeof payload === "string") {
    const message = payload.trim();
    return message || null;
  }

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const message = record.message ?? record.error;

    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }

  return null;
}

async function getErrorMessage(response: Response): Promise<string> {
  const fallbackMessage = `HTTP ${response.status}`;

  try {
    const contentType = response.headers.get("content-type") ?? "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return extractErrorMessage(payload) ?? fallbackMessage;
    }

    const payload = await response.text();
    return extractErrorMessage(payload) ?? fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

const ERROR_MAP: [RegExp, string][] = [
  [/unused respond to webhook/i, "GV Intelligence backend is nog niet goed ingesteld in n8n. Neem contact op met de beheerder."],
  [/ECONNREFUSED|failed to fetch|networkerror/i, "Kan geen verbinding maken met de GV Intelligence server. Controleer of de tunnel actief is."],
  [/timeout|timed out/i, "Het verzoek duurde te lang. Probeer het later opnieuw."],
];

function mapToFriendlyError(raw: string): string {
  for (const [pattern, message] of ERROR_MAP) {
    if (pattern.test(raw)) return message;
  }
  return `Verbinding mislukt: ${raw}`;
}

export async function fetchGVIntelligence(query: string, mode = resolveGVIntelligenceMode(query)): Promise<GVIntelligenceResult> {
  const response = await fetch(GV_INTELLIGENCE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
    body: JSON.stringify({ query, mode }),
  });

  if (!response.ok) {
    const raw = await getErrorMessage(response);
    const friendly = mapToFriendlyError(raw);
    const err = new Error(friendly);
    (err as any).technicalDetail = raw;
    throw err;
  }

  const payload = await response.json() as Record<string, unknown>;

  return {
    answer: typeof payload.answer === "string" && payload.answer.trim() ? payload.answer : "Geen antwoord ontvangen.",
    sources: Array.isArray(payload.sources) ? payload.sources as GVIntelligenceSource[] : [],
    timestamp: typeof payload.timestamp === "string" ? payload.timestamp : undefined,
    model: typeof payload.model === "string" ? payload.model : undefined,
  };
}